import React from 'react';
import { useNavigate } from 'react-router-dom';
import { analytics, useCurrentAgreement, useCurrentUser, useHost, useHostRefetch } from '@economic/react-app';
import { setLocale } from 'yup';
import { t } from '@lingui/macro';
import { setupHostEndpoints } from '~/app/shared/util/api/setupHostEndpoints';
import { useLocalization } from '~/app/shared/components/l10n/l10n';
import { legacyRouteTexts } from '~/utilities/routeTextsMap';
import initSnowplow from '~/utilities/snowplow';

export const useEconomic = () => {
    const navigate = useNavigate();
    const agreement = useCurrentAgreement();
    const user = useCurrentUser();
    const host = useHost();
    const refetch = useHostRefetch();

    // fetch global, preloaded texts from Startup component... ideally we replace this with new translations but that is a huge task
    const loading = useLocalization(globalTextIds);

    React.useEffect(() => {
        setupHostEndpoints(host.services);

        if (window.top) {
            window.top.document.title = `${agreement.number} - ${agreement.name}`;

            // provides an integration for legacy e-conomic to trigger route changes
            window.top.gotoRoute = (to, state = {}) => {
                if (to.startsWith('#')) {
                    navigate(window.location.pathname + to, { state });
                } else {
                    navigate(to, { state });
                }
            };

            window.top.refreshRouting = async () => {
                refetch();
                return Promise.resolve();
            };

            // provides an integration for legacy e-conomic to trigger google tag manager events
            window.top.gtmEvent = analytics.gtm;
        }
    }, []);

    React.useEffect(() => {
        // Place any logic to run at startup here
        if (!loading) {
            setYupLocale();
            const enableSnowplow = agreement.features.snowplowTrackingJournals;
            initSnowplow({
                snowplowUrl: host.services.snowplow,
                userGuid: user.guid,
                agreementNumber: agreement.number,
                enableSnowplow,
            });
        }
    }, [loading]);

    return loading;
};

export function setYupLocale(): void {
    setLocale({
        mixed: {
            required: t`Required`,
            notType: t`Technical error (type)`,
        },
        number: {
            min: ({ min }) => {
                return t`Must be greater than or equal to ${min}`;
            },
            max: ({ max }) => {
                return t`Must be less than or equal to ${max}`;
            },
        },
        string: {
            min: ({ min }) => {
                return t`Min. ${min} characters`;
            },
            max: ({ max }) => {
                return t`Max. ${max} characters`;
            },
        },
    });
}

export const globalTextIds = [
    ...Object.values(legacyRouteTexts),
    'TOAST_LOADING',
    'FIREBIRD_FORM_CUSTOMERS_TAB_GENERAL',
    'FIREBIRD_FORM_CUSTOMERS_TAB_ADDITIONAL',
    'FIREBIRD_FORM_CUSTOMERS_TAB_DELIVERY',
    'FIREBIRD_FORM_CUSTOMERS_TAB_PRICING',
    'MODAL_CURRENCIES_TITLE',
    'MODAL_CHART_OF_ACCOUNTS_TITLE',
    'LEFTMENU_SETTINGS_DEPARTMENTS',
    'FORM_DELIVERYPLACE_HEADLINE',
    'FIREBIRD_FORM_CUSTOMERS_LISTHEADLINE',
    'FORM_EXPENSES_HEADLINE',
    'FORM_EXPENSEGROUPS_HEADLINE',
    'FORM_CONTACTS_HEADLINE',
    'FORM_EMPLOYEEGROUPS_HEADLINE',
    'FORM_PRICEGROUP_HEADLINE',
    'FORM_CASETASKEMPLOYEES_HEADLINE',
    'FORM_CASES_APPLETHEADLINE',
    'FORM_GOODSGROUPS_HEADLINE',
    'TOAST_DOCUMENT_REQUEST_ATTACH_SUCCESS',
    'TOAST_INBOX_WORKFLOW_ATTACH_ERROR',
    'DOCUMENT_INBOX_TOOLTIP_MOVE_TO_TOP',
    'DOCUMENT_INBOX_TOOLTIP_MOVE_UP',
    'DOCUMENT_INBOX_TOOLTIP_MOVE_DOWN',
    'DOCUMENT_INBOX_TOOLTIP_MOVE_TO_BOTTOM',
    'DOCUMENT_INBOX_TOOLTIP_SMARTSCAN_STATUS_ERROR',
    'FORM_ACCOUNTS_CREATETEXT',
    'FORM_PAYABLES_CREATETEXT',
    'FORM_EMPLOYEES_CREATETEXT',
    'FORM_EMPLOYEEGROUPS_CREATETEXT',
    'FORM_PRICEGROUP_CREATETEXT',
    'FORM_REBATEGROUP_CREATETEXT',
    'FORM_DEPARTMENT_CREATETEXT',
    'FORM_RECEIVABLES_CREATETEXT',
    'FORM_RECEIVABLES_ERROR_CUSTOMERNONOTFOUND',
    'FORM_RECEIVABLESGROUPS_CREATETEXT',
    'FORM_EXPENSES_CREATETEXT',
    'FORM_EXPENSEGROUPS_CREATETEXT',
    'FORM_STOCKREBATEGROUP_HEADLINE',
    'FORM_LOCATION_HEADLINE',
    'FORM_CATEGORY_HEADLINE',
    'TABLE_COLUMN_ENTRY_TYPE',
    'TABLE_COLUMN_ENTRY_NUMBER',
    'TABLE_COLUMN_ENTRY_DATE',
    'TABLE_COLUMN_ENTRY_TEXT',
    'TABLE_COLUMN_ENTRY_AMOUNT',
    'TABLE_COLUMN_ENTRY_ACCOUNT_NUMBER',
    'TABLE_COLUMN_ENTRY_CONTRA_ACCOUNT_NUMBER',
    'TABLE_COLUMN_ENTRY_CURRENCY',
    'FORM_FIELD_PAYMENT_DETAILS_OPTION_NONE',
    'FORM_FIELD_PAYMENT_DETAILS_OPTION_BANK_TRANSFER',
    'FORM_FIELD_PAYMENT_DETAILS_OPTION_ENTRY_ONLY',
    'ERROR_CODE_E04000',
    'ERROR_CODE_E06830',
    'ERROR_CODE_E06841',
    'ERROR_CODE_E06850',
    'ERROR_CODE_E06880',
    'ERROR_CODE_E06890',
    'ERROR_CODE_E07020',
    'ERROR_CODE_E07031',
    'ERROR_CODE_E07040',
    'ERROR_CODE_E07070',
    'ERROR_CODE_E07090',
    'ERROR_CODE_E07130',
    'ERROR_CODE_E07140',
    'ERROR_CODE_E07150',
    'ERROR_CODE_E07190',
    'ERROR_CODE_E08002',
    'ERROR_CODE_E08003',
    'ERROR_CODE_E08004',
    'ERROR_CODE_E08005',
    'ERROR_CODE_E04030',
    'ERROR_CODE_E06520',
    'ERROR_CODE_AccountDoesNotExist',
    'ERROR_CODE_AccountIsNotBalanceOrProfitAndLossType',
    'ERROR_CODE_AccountIdChangeNotAllowed',
    'ERROR_CODE_AlreadyExists',
    'ERROR_CODE_DepartmentIsBarred',
    'ERROR_CODE_InvalidFieldValue',
    'ERROR_CODE_FieldValueLengthTooLong',
    'ERROR_CODE_ModuleNotActive',
    'ERROR_CODE_RequiredField',
    'ERROR_CODE_ProductNumberAlreadyExists',
    'ERROR_CODE_ProductNumberChangeSettingNotEnabled',
    'ERROR_CODE_ProductUsedInWebshop',
    'ERROR_CODE_ProductGroupDoesNotExist',
    'ERROR_CODE_ProductGroupWithWithdrawalCanNotBeChangedToGroupWithService',
    'ERROR_CODE_ProductGroupChangeDisallowedByWebShop',
    'ERROR_CODE_ProductGroupAlreadyExists',
    'ERROR_CODE_UsedByCustomerGroup',
    'ERROR_CODE_CannotChangeProductGroupNumber',
    'ERROR_CODE_CannotDisableInventoryWithdrawal',
    'ERROR_CODE_InventoryWithdrawalCannotHaveAccruals',
    'ERROR_CODE_CannotDeleteProductGroupConnectedToProducts',
    'ERROR_CODE_ProductUsedOnInvoiceLines',
    'ERROR_CODE_ProductUsedOnDraftInvoiceLines',
    'ERROR_CODE_ProductUsedInInventoryMovements',
    'ERROR_CODE_ProductUsedInSubscriptions',
    'ERROR_CODE_InvalidAmount',
    'ERROR_CODE_InvalidAmountValue',
    'ERROR_CODE_SMARTBANK_ERROR_ACCOUNT_DOES_NOT_EXIST',
    'ERROR_CODE_SMARTBANK_ERROR_PAYMENT_ACCOUNT_ALREADY_PAIRED',
    'ERROR_CODE_SupplierNumberChangeNotAllowed',
    'ERROR_CODE_COMPANY_GLN_REGISTERED_FOR_NEMHANDEL',
    'ERROR_CODE_CVR_INVALID',
    'ERROR_CODE_InvalidUrl',
    'ERROR_CODE_InvalidName',
    'ERROR_CODE_AlreadyExistingNameOrUrl',
    'ERROR_CODE_InvalidAccountId',
    'ERROR_CODE_InvalidAccountType',
    'ERROR_CODE_InvalidAccountName',
    'ERROR_CODE_AccountIdAlreadyInUse',
    'ERROR_CODE_TotalFromAccountDoesNotExist',
    'ERROR_CODE_TotalIntervalsValueContainsInvalidChars',
    'ERROR_CODE_IntervalNotConsturctedCorrectly',
    'ERROR_CODE_IntervalHasOverlappingValues',
    'ERROR_CODE_BarredAccountUsedByProductGroups',
    'ERROR_CODE_BarredAccountUsedByVatAccounts',
    'ERROR_CODE_CustomerInvalidCustomerNumber',
    'ERROR_CODE_CustomerAlreadyExists',
    'ERROR_CODE_CustomerInvalidEmail',
    'ERROR_CODE_CustomerInvalidInvoiceEmail',
    'ERROR_CODE_CustomerInvalidOrderEmail',
    'ERROR_CODE_CustomerInvalidQuotationEmail',
    'ERROR_CODE_CustomerInvalidReminderEmail',
    'ERROR_CODE_CustomerInvalidStatementEmail',
    'ERROR_CODE_CustomerInvalidPNumber',
    'ERROR_CODE_CustomerInvalidDiscount',
    'ERROR_CODE_CustomerInvalidDiscountGroup',
    'ERROR_CODE_CustomerInvalidPriceGroup',
    'ERROR_CODE_CustomerannotChangeNumber',
    'ERROR_CODE_CustomerMissingName',
    'ERROR_CODE_CustomerInvalidGroupId',
    'ERROR_CODE_CustomerInvalidVatZone',
    'ERROR_CODE_CustomerInvalidVoucher',
    'ERROR_CODE_CustomerInvalidPaymentTerm',
    'ERROR_CODE_CustomerInvalidCharacters',
    'ERROR_CODE_CustomerGroupInvalidFieldValue',
    'ERROR_CODE_CustomerGroupExists',
    'ERROR_CODE_CustomerGroupAcountDoesNotExist',
    'ERROR_CODE_CustomerGroupAccountIsNotBalanceOrProfitAndLossType',
    'ERROR_CODE_CustomerGroupAccountAppliedForProductGroups',
    'ERROR_CODE_CustomerGroupDiscountNotInRange',
    'FORM_FIELD_ERROR_DATE_NOT_WITHIN_EXISTING_ACCOUNTING_YEAR',
    'FORM_FIELD_ERROR_DUPLICATE_INVOICE_NUMBER',
    'FORM_FIELD_ERROR_TYPE',
    'GUIDED_TOUR_BUTTON_BACK',
    'GUIDED_TOUR_BUTTON_CLOSE',
    'GUIDED_TOUR_BUTTON_DONE',
    'GUIDED_TOUR_BUTTON_NEXT',
    'GUIDED_TOUR_BUTTON_SKIP',
    'FORM_LISTVIEW_FILTER_PLACEHOLDER',
    'INBOX_GENERATING_EXPORT_TO_EXCEL',
    'FORM_ASSETS_GROUP_HEADLINE',
    'FORM_ASSETS_GROUP_ADD_NEW',
    'FORM_LEDGER_EDITFIELD_ASSETNUMBER_WARNING_ALREADYINUSE',
    'FORM_ASSETS_GROUP_HEADLINE_APPLET',
    'FORM_FIELD_LABEL_USEFUL_LIFE',
    'FORM_FIELD_LABEL_RESIDUAL_VALUE',
    'FORM_FIELD_LABEL_ADDITIONS_ACCOUNT',
    'FORM_FIELD_LABEL_DEPRECIATION_BALANCE_SHEET_ACCOUNT',
    'FORM_FIELD_LABEL_DEPRECIATION_PROFIT_LOSS_ACCOUNT',
    'FORM_FIELD_LABEL_DEPRECIATION_REVERSED_ACCOUNT',
    'FORM_FIELD_LABEL_DISPOSALS_ACCOUNT',
    'FORM_FIELD_LABEL_NET_BOOKED_VALUE_ACCOUNT',
    'FORM_FIELD_LABEL_ADDITIONS_CONTRA_ACCOUNT',
    'FORM_FIELD_LABEL_DEPRECIATION_CONTRA_ACCOUNT',
    'DISTRIBUTION_TEMPLATE_DROPDOWN_OPTION_GROSS',
    'DISTRIBUTION_TEMPLATE_DROPDOWN_OPTION_NET',
    'DISTRIBUTION_TEMPLATE_HEADING',
    'DISTRIBUTION_TEMPLATE_NEW_ROW',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_ACCOUNTNUMBER',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_AMOUNT',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_ACCOUNTNAME',
    'DAYBOOK_NAVIGATION_LIST_DEPARTMENT',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_QUANTITY1',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_QUANTITY2',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_VAT',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_DEBIT_OR_CREDIT',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_GROSS_OR_NET',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_TEXT',
    'DISTRIBUTION_TEMPLATE_AMOUNT_FOR_DISTRIBUTION',
    'DISTRIBUTION_TEMPLATE_APPLY_TEMPLATE',
    'DISTRIBUTION_TEMPLATE_SAVE_TEMPLATE',
    'DISTRIBUTION_TEMPLATE_NEW_TEMPLATE',
    'DISTRIBUTION_TEMPLATE_REMAINDER',
    'DISTRIBUTION_TEMPLATE_USE_EXISTING_TEMPLATE',
    'DISTRIBUTION_TEMPLATE_SAVE_TITLE',
    'DISTRIBUTION_TEMPLATE_SAVE_TEMPLATENAME',
    'DISTRIBUTION_TEMPLATE_SAVE_AMOUNT',
    'DISTRIBUTION_TEMPLATE_SAVE_TEXT',
    'DISTRIBUTION_TEMPLATE_SAVE_DEPARTMENT',
    'DISTRIBUTION_TEMPLATE_SAVE_QUANTITY1',
    'DISTRIBUTION_TEMPLATE_SAVE_QUANTITY2',
    'DISTRIBUTION_TEMPLATE_VIEW_DOCUMENT',
    'EONBOARDING_CVRVALIDATION_STATUS_MISMATCH_DETAILS',
    'EONBOARDING_CVRVALIDATION_STATUS_INUSE_DETAILS',
    'EONBOARDING_CVRVALIDATION_STATUS_INVALID_DETAILS',
    'EONBOARDING_CVRVALIDATION_CVRSTATUS',
    'EONBOARDING_CVRVALIDATION_COMPANY_NAME',
    'EONBOARDING_CVRVALIDATION_STATUS_INVALID',
    'EONBOARDING_CVRVALIDATION_STATUS_INUSE',
    'EONBOARDING_CVRVALIDATION_STATUS_VALID',
    'EONBOARDING_CVRVALIDATION_STATUS_INVALID_GLN_DETAILS',
    'EONBOARDING_CVRVALIDATION_STATUS_NOT_ELIGIBLE_FOR_FREE_GLN_DETAILS',
    'EONBOARDING_REGISTRATION_ERROR',
    'EONBOARDING_REGISTRATION_ERROR_DUPLICATE_CVR',
    'EONBOARDING_LOOKUP_ERROR',
    'EONBOARDING_TEXT_HEADER',
    'EONBOARDING_TEXT_SUBHEADER',
    'EONBOARDING_TOC_CHECKBOX',
    'EONBOARDING_LEARN_MORE_BUTTON',
    'EONBOARDING_LEARN_MORE_BUTTON_URL',
    'EONBOARDING_ACTIVATE_BUTTON',
    'EONBOARDING_CVR_NUMBER_LABEL',
    'EONBOARDING_SUCCESS_HEADLINE',
    'EONBOARDING_SUCCESS_SUBHEADER',
    'EONBOARDING_SUCCESS_GETSTARTED_BUTTON',
    'EONBOARDING_SUCCESS_CONTACTPOINT_ECOPEDIA',
    'EONBOARDING_SUCCESS_CONTACTPOINT_ECOPEDIA_LINK',
    'EONBOARDING_SUCCESS_CONTACTPOINT_ECOPEDIA_LINK_URL',
    'EONBOARDING_SUCCESS_CONTACTPOINT_SUPPORT',
    'EONBOARDING_SUCCESS_CONTACTPOINT_SUPPORT_PHONENO',
    'EONBOARDING_SPLASH_HEADLINE',
    'EONBOARDING_SPLASH_SUBTITLE',
    'EONBOARDING_SPLASH_SELLINGPOINT_ONE',
    'EONBOARDING_SPLASH_SELLINGPOINT_TWO',
    'EONBOARDING_SPLASH_SELLINGPOINT_THREE',
    'EONBOARDING_SPLASH_LEARN_MORE_BUTTON_TEXT',
    'EONBOARDING_SPLASH_LEARN_MORE_BUTTON_URL',
    'EONBOARDING_SPLASH_CONTINUE_BUTTON_TEXT',
    'LABEL_LANGUAGE_DANISH',
    'LABEL_LANGUAGE_ENGLISH',
    'HANGER_INBOX',
    'HEADER_SETTINGS_BUTTON_HANGER_TITLE',
    'HEADER_SETTINGS_BUTTON_HANGER_DESCRIPTION',
    'HEADER_TAB_LIVECHAT',
    'HEADER_TAB_INBOX',
    'HEADER_TAB_SMARTINBOX',
    'HEADER_TAB_NEW_BADGE',
    'HEADER_TAB_MARKET',
    'HEADER_TAB_INTERCOM',
    'HEADER_TAB_ECOPEDIA',
    'HEADER_TAB_SETTINGS',
    'HEADER_TAB_NOTIFICATIONS',
    'NOTIFICATIONS_PANEL_MARK_ALL_READ_BUTTON',
    'NOTIFICATIONS_PANEL_NOTIFICATION_MARK_AS_READ',
    'NOTIFICATIONS_JUST_NOW',
    'NOTIFICATIONS_LAST_HOUR',
    'NOTIFICATIONS_ONE_HOUR_AGO',
    'NOTIFICATIONS_N_HOURS_AGO',
    'NOTIFICATIONS_N_MINUTES_AGO',
    'NOTIFICATIONS_TODAY',
    'NOTIFICATIONS_YESTERDAY',
    'NOTIFICATIONS_N_DAYS_AGO',
    'NOTIFICATIONS_ONE_WEEK_AGO',
    'NOTIFICATIONS_N_WEEKS_AGO',
    'NOTIFICATIONS_TYPES_CUSTOM_NAME',
    'NOTIFICATIONS_TYPES_CUSTOM_WELCOME_TO_NOTIFICATIONS',
    'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_NAME',
    'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_INFO',
    'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_NAME',
    'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_INFO',
    'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_NAME',
    'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_INFO',
    'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_NAME',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_GROUPED_INFO',
    'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_NAME',
    'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_INFO',
    'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_GROUPED_INFO',
    'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_NAME',
    'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_INFO',
    'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_NAME',
    'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_INFO',
    'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_NAME',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_GROUPED_INFO',
    'NYETS_FORM_ACCOUNTS_EDITFIELD_MIRRORCODE',
    'SMARTPAY_MENU_HANGER_TEXT',
    'DOCUMENT_INBOX_DOCUMENT_SETTINGS_SUGGESTIONS_TITLE',
    'DOCUMENT_INBOX_DOCUMENT_SUGGESTIONS_SETTING_DESCRIPTION',
    'DOCUMENT_INBOX_DOCUMENT_SUGGESTIONS_SETTING_INTRODUCTION',
    'TOAST_DISTRIBUTION_TEMPLATE_UPDATE_SUCCESS',
    'TOAST_DISTRIBUTION_TEMPLATE_CREATE_SUCCESS',
    'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_NAME',
    'NOTIFICATIONS_TYPES_ELECTRONIC_DOCUMENT_RECEIVED',
    'NOTIFICATIONS_TYPES_DOCUMENTS_SENT_FOR_APPROVAL',
    'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_INFO',
    'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_GROUPED_INFO',
    'ENTRYNUMBER_DROPDOWN_ASSIGN_MANUALLY',
    'ENTRYNUMBER_DROPDOWN_USE_SAME',
    'ENTRYNUMBER_DROPDOWN_USE_CONTINUOUS',
    'DIALOG_CHANGE_NUMBER',
    'FORM_ACCOUNTS_CHANGENO_HEADLINE',
    'FORM_ACCOUNTS_CHANGENO_CONFIRM',
    'FORM_ACCOUNTS_APPLETHEADLINE',
    'GIRO_ACCOUNT_VALIDATION_MESSAGE',
];
